import React, { useState, useEffect } from 'react';
import { getPendingAccessRequests, approveAccessRequest } from '../../utils/adminApi';
import './AccessRequestsTab.css';

const AccessRequestsTab = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [groupName, setGroupName] = useState('friends');
  const [personalizedMessage, setPersonalizedMessage] = useState('');

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getPendingAccessRequests();
      setRequests(data.requests);
    } catch (err) {
      setError('Failed to load access requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    setSelectedRequest(request);
    // Modal will open for group selection and message
  };

  const confirmApprove = async () => {
    setProcessingRequest(selectedRequest.requestId);
    setError(null);
    
    try {
      await approveAccessRequest({
        requestId: selectedRequest.requestId,
        approved: true,
        groupName,
        personalizedMessage: personalizedMessage.trim()
      });
      
      alert(`Access approved for ${selectedRequest.name}!`);
      setSelectedRequest(null);
      setPersonalizedMessage('');
      setGroupName('friends');
      loadRequests();
    } catch (err) {
      setError('Failed to approve request');
      console.error(err);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleDeny = async (requestId) => {
    if (!window.confirm('Are you sure you want to deny this request?')) {
      return;
    }
    
    setProcessingRequest(requestId);
    setError(null);
    
    try {
      await approveAccessRequest({
        requestId,
        approved: false,
        personalizedMessage: personalizedMessage.trim()
      });
      
      alert('Request denied');
      loadRequests();
    } catch (err) {
      setError('Failed to deny request');
      console.error(err);
    } finally {
      setProcessingRequest(null);
    }
  };

  if (loading) {
    return <div className="tab-loading">Loading access requests...</div>;
  }

  return (
    <div className="access-requests-tab">
      <h2>Pending Access Requests</h2>
      
      {error && (
        <div className="error-banner">{error}</div>
      )}
      
      {requests.length === 0 && (
        <div className="no-requests">
          <p>No pending access requests</p>
        </div>
      )}
      
      {requests.map(request => (
        <div key={request.requestId} className="request-card">
          <div className="request-header">
            <h3>{request.name}</h3>
            <span className="request-date">
              {new Date(request.requestDate).toLocaleDateString()}
            </span>
          </div>
          
          <div className="request-body">
            <p><strong>Email:</strong> {request.email}</p>
            <p><strong>Relationship:</strong> {request.relationship}</p>
            {request.message && (
              <div className="request-message">
                <strong>Message:</strong>
                <p>{request.message}</p>
              </div>
            )}
          </div>
          
          <div className="request-actions">
            <button
              onClick={() => handleApprove(request)}
              disabled={processingRequest === request.requestId}
              className="approve-button"
            >
              Approve
            </button>
            <button
              onClick={() => handleDeny(request.requestId)}
              disabled={processingRequest === request.requestId}
              className="deny-button"
            >
              Deny
            </button>
          </div>
        </div>
      ))}
      
      {selectedRequest && (
        <div className="approval-modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div className="approval-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Approve Access for {selectedRequest.name}</h3>
            
            <div className="modal-field">
              <label>Assign to Group:</label>
              <select
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              >
                <option value="friends">Friends</option>
                <option value="family">Family</option>
              </select>
            </div>
            
            <div className="modal-field">
              <label>Personalized Message (Optional):</label>
              <textarea
                value={personalizedMessage}
                onChange={(e) => setPersonalizedMessage(e.target.value)}
                placeholder="Add a personal note to the approval email..."
                rows="4"
              />
              <p className="field-hint">
                Leave blank to use the default approval message
              </p>
            </div>
            
            <div className="modal-actions">
              <button onClick={() => setSelectedRequest(null)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={confirmApprove} className="confirm-btn">
                Approve & Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessRequestsTab;